import { COMPANY_NAME } from '../constants'
import { ROLE_IMAGES, RoleImage } from './roleImages'

export type SkillGroupIds =
  | 'Maintenance'
  | 'Housekeeping'
  | 'Dishwasher'
  | 'Barback'
  | 'Prep Cook'
  | 'Baker/Pastry Chef'
  | 'Event Setup'
  | 'Line Cook'
  | 'FOH Support'
  | 'Bartender'
  | 'Stage'

export type SkillGroup = {
  id: SkillGroupIds
  label?: string
  slug: string
  closestGigproSkillGroupId: number
  image?: RoleImage
  gigsSlug?: string
  pageContentTitle?: (market: string) => string
  pageContentDescription?: (market: string) => string
}

export const SKILL_GROUPS: Array<SkillGroup> = [
  {
    id: 'Dishwasher',
    slug: 'dishwasher',
    closestGigproSkillGroupId: 6,
    image: ROLE_IMAGES.dishwasher,
    pageContentTitle: (market) => `Dishwashers for Hire in ${market}`,
    pageContentDescription: (market) =>
      `A clean kitchen is the cornerstone of any successful restaurant. Keep your operations hygienic and efficient by hiring reliable Dishwashers from ${COMPANY_NAME}. Our dishwashers have experience in a variety of settings, ensuring your kitchen runs smoothly. Browse through the profiles below to find the perfect dishwasher for your needs. Find and hire Dishwashers on ${COMPANY_NAME} in ${market}, the leading platform for on-demand hospitality staffing.`,
  },
  {
    id: 'Line Cook',
    closestGigproSkillGroupId: 3,
    slug: 'line-cook',
    image: ROLE_IMAGES.lineCook,
    pageContentTitle: (market) => `Line Cooks for Hire in ${market}`,
    pageContentDescription: (market) =>
      `A busy kitchen needs a reliable line cook to keep things moving efficiently. Avoid the chaos and hire experienced Line Cooks from ${COMPANY_NAME} to ensure seamless service. Whether you run a casual eatery or an upscale restaurant, our line cooks are equipped with the skills you need. Explore the profiles below to find the perfect line cook for your kitchen. Find and hire Line Cooks on ${COMPANY_NAME} in ${market}, the leading platform for on-demand hospitality staffing.`,
  },
  {
    id: 'FOH Support',
    closestGigproSkillGroupId: 9,
    slug: 'foh-Support',
    gigsSlug: 'foh-support',
    image: ROLE_IMAGES.server,
    pageContentTitle: (market) => `FOH Support for Hire in ${market}`,
    pageContentDescription: (market) =>
      `Smooth front-of-house operations are crucial for guest satisfaction. Ensure exceptional service by hiring experienced FOH Support staff from ${COMPANY_NAME}. Our gig workers (we call them “Pros”) excel in various roles, from hosting to bussing tables, ensuring your guests have a memorable experience. Check out the profiles below to find the right FOH support for your establishment. Find and hire FOH Support staff on ${COMPANY_NAME} in ${market}, the premier platform for on-demand hospitality staffing.`,
  },
  {
    id: 'Prep Cook',
    closestGigproSkillGroupId: 4,
    slug: 'prep-cook',
    image: ROLE_IMAGES.lineCook,
    pageContentTitle: (market) => `Prep Cooks for Hire in ${market}`,
    pageContentDescription: (market) =>
      `Having no prep done before service is a restaurant manager’s nightmare. Stay on top of your kitchen’s operations and find qualified, experienced Prep Cooks in ${market} to fill in when the need arises. Our Prep Cooks have experience that runs the gamut from quick service restaurants to fine dining establishments - check out the profiles below to see what kind of talent you can find on ${COMPANY_NAME}. Find and hire Prep Cooks on ${COMPANY_NAME} in ${market}, the top platform for on-demand hospitality staffing.`,
  },
  {
    id: 'Housekeeping',
    closestGigproSkillGroupId: 12,
    slug: 'housekeeping',
    image: ROLE_IMAGES.housekeeping,
    pageContentTitle: (market) => `Housekeeping Staff for Hire in ${market}`,
    pageContentDescription: (market) =>
      `Maintaining cleanliness and order is vital for guest satisfaction. Ensure your establishment shines by hiring qualified Housekeepers from ${COMPANY_NAME}. Our housekeepers in ${market} have experience in a range of hospitality settings, guaranteeing a spotless environment for your guests. Check out the profiles below to find reliable housekeeping staff ready to meet your needs. Find and hire Housekeeping Staff on ${COMPANY_NAME} in ${market}, your trusted source for on-demand hospitality staffing.`,
  },
  {
    id: 'Baker/Pastry Chef',
    label: 'Baker/Pastry',
    closestGigproSkillGroupId: 14,
    slug: 'baker-pastry-chef',
    gigsSlug: 'baker-pastry-cook',
    image: ROLE_IMAGES.baker,
    pageContentTitle: (market: string) => `Pastry Cooks for Hire in ${market}`,
    pageContentDescription: (market: string) =>
      `Creating delightful desserts requires a skilled pastry cook. Enhance your kitchen’s offerings by hiring talented Pastry Cooks from ${COMPANY_NAME}. Our pastry cooks have honed their skills in various settings, from boutique bakeries to high-end restaurants. Browse through the profiles below to find the perfect pastry expert for your needs. Find and hire Pastry Cookson ${COMPANY_NAME} in ${market}, the top platform for on-demand hospitality staffing.`,
  },
  {
    id: 'Maintenance',
    label: 'General Labor',
    closestGigproSkillGroupId: 13,
    slug: 'maintenance',
    image: ROLE_IMAGES.maintenance,
    pageContentTitle: (market: string) =>
      `Maintenance Staff for Hire in ${market}`,
    pageContentDescription: (market: string) =>
      `Unexpected maintenance issues can disrupt your operations and frustrate guests. Keep everything running smoothly by hiring skilled Maintenance Staff from ${COMPANY_NAME}. Our maintenance gig workers (we call them “Pros”) are experienced in handling a wide range of issues, ensuring that your establishment stays in top shape. Check out the profiles below to find reliable maintenance workers available when you need them. Find and hire Maintenance Staff on ${COMPANY_NAME} in ${market}, your trusted source for on-demand hospitality staffing.`,
  },
  {
    id: 'Bartender',
    closestGigproSkillGroupId: 11, // ID of the full service bar
    slug: 'bartender',
    /** To be used when this skill group needs to get sent to a jobs page. */
    gigsSlug: 'bartender-full-service-bar',
    image: ROLE_IMAGES.bartender,
    pageContentTitle: (market: string) => `Bartenders for Hire in ${market}`,
    pageContentDescription: (market: string) =>
      `A skilled bartender can be the highlight of any event or night out. Enhance your bar’s service by hiring experienced Bartenders from ${COMPANY_NAME}. Our Bartenders are adept at crafting cocktails and providing excellent customer service in any setting. Browse through the profiles below to find the perfect bartender for your needs. Find and hire Bartenders on ${COMPANY_NAME} in ${market}, the premier platform for on-demand hospitality staffing.`,
  },
  {
    id: 'Barback',
    closestGigproSkillGroupId: 10,
    slug: 'barback',
    image: ROLE_IMAGES.bartender,
    pageContentTitle: (market: string) => `Barbacks for Hire in ${market}`,
    pageContentDescription: (market: string) =>
      `A well-stocked bar is essential for quick service and happy customers. Ensure your bar runs smoothly by hiring experienced Barbacks from ${COMPANY_NAME}. Our barbacks are skilled in keeping the bar organized and assisting bartenders efficiently. Check out the profiles below to find the right barback for your establishment. Find and hire Barbacks on ${COMPANY_NAME} in ${market}, the premier platform for on-demand hospitality staffing.`,
  },
  {
    id: 'Event Setup',
    closestGigproSkillGroupId: 7, // event server
    slug: 'event-setup',
    gigsSlug: 'event-server',
    image: ROLE_IMAGES.server,
    pageContentTitle: (market: string) =>
      `Event Setup/Breakdown Crew for Hire in ${market}`,
    pageContentDescription: (market: string) =>
      `Planning an event is challenging enough without the added stress of setup logistics. Ensure your event runs smoothly from the start with experienced Event Setup Crew members in ${market} from ${COMPANY_NAME}. Our gig workers (we call them “Pros”) have a wealth of experience setting up for a variety of events, from corporate functions to large-scale weddings. Browse through the profiles below to help with your event staffing today. Find and hire Event Setup Crew members on ${COMPANY_NAME}, the premier Gig app for on-demand hospitality staffing.`,
  },
  {
    id: 'Stage',
    slug: 'stage',
    closestGigproSkillGroupId: 15,
    image: ROLE_IMAGES.lineCook,
    pageContentTitle: (market: string) => `Hire Culinary Staff in ${market}`,
    pageContentDescription: (market: string) =>
      `Staging provides a valuable opportunity to identify and nurture potential future employees. By bringing in stagiaires, a restaurant can observe their skills, work ethic, and cultural fit firsthand, making it easier to identify top talent for permanent positions. For aspiring culinary professionals, staging offers a chance to gain hands-on experience in a fast-paced and dynamic kitchen environment. Post your Stage role on ${COMPANY_NAME} in ${market} , the premier platform for on-demand culinary staffing.`,
  },
]

export const CONNECT_MODULE_SKILL_GROUPS = SKILL_GROUPS.filter(
  (skillGroup) => skillGroup.id !== 'Stage',
)
